import { matchStandard, standardFronts, standardGroupFor } from '../cabinets/standardMatch';
import type { Cabinet } from '../cabinets/model';
import { unitName, type CabinetCatalogue, type CabinetProduct } from './cabinetCatalogue';
import { NeedsPriceRequest, type CabinetOrderSpec, type CabinetPricing, type CabinetQuote } from './cabinetPricing';
import { browserCartTokens, withBasket, type CartTokenStore } from './doorOrders';
import type { DoorOrderSpec, DoorPricing } from './doorPricing';
import { API_ROOT, ApiError, http, type Fetch } from './http';

/**
 * The made-to-measure tab's standard cabinets, priced and sold for real.
 *
 * <p>Each cabinet is matched to the published standard unit it is
 * ({@link matchStandard}). The carcass is that unit's product at this
 * shopper's price, read from the same price list as the "Standard sizes" tab;
 * its fronts, in the order's door range and colour, go to the server's door
 * pricing and door basket, cut to the supplier's own front sizes. An order
 * with any cabinet that is not a standard unit left as it is is refused with
 * {@link NeedsPriceRequest}: the shop prices those by hand.
 *
 * <p>Fronts painted to order go to the server with the colour the shopper
 * named, as on the doors page; until one is named they cannot be priced.
 */

export interface StandardCabinets {
  /** Cabinet i's standard unit, or undefined where it was modified. */
  match(cabinets: readonly Cabinet[]): Promise<Array<CabinetProduct | undefined>>;
}

export function standardCabinets(catalogue: CabinetCatalogue): StandardCabinets {
  let groups: Promise<Map<string, string>> | null = null;
  const products = new Map<string, Promise<CabinetProduct[]>>();

  // Builder group ("base") -> the kitchen range's group id ("standard:base").
  const groupIds = () => {
    if (!groups) {
      groups = catalogue.groups().then((list) => {
        const out = new Map<string, string>();
        for (const g of list) {
          const short = g.id.slice(g.id.indexOf(':') + 1);
          if (!out.has(short)) out.set(short, g.id);
        }
        return out;
      });
      groups.catch(() => {
        groups = null;
      });
    }
    return groups;
  };
  const productsOf = (groupId: string) => {
    let held = products.get(groupId);
    if (!held) {
      held = catalogue.products(groupId);
      products.set(groupId, held);
      held.catch(() => products.delete(groupId));
    }
    return held;
  };

  return {
    async match(cabinets) {
      const ids = await groupIds();
      return Promise.all(
        cabinets.map(async (c) => {
          const short = standardGroupFor(c);
          const groupId = short ? ids.get(short) : undefined;
          return groupId ? matchStandard(c, await productsOf(groupId)) : undefined;
        }),
      );
    },
  };
}

function name(unit: CabinetProduct): string {
  return `${unitName(unit.name)}, ${unit.w}mm`;
}

/** The door order for one cabinet's fronts, or null for a carcass only. */
function frontsOrder(spec: CabinetOrderSpec, c: Cabinet, unit: CabinetProduct): DoorOrderSpec | null {
  const pieces = standardFronts(unit, c, spec.handle);
  if (pieces.length === 0) return null;
  // The shop supplies no handles or hinges: the fronts are drilled for the shopper's own.
  return {
    rangeId: spec.rangeId,
    colourId: spec.colourId,
    ...(spec.colourId === 'ral' ? { paintColour: spec.paintColour?.trim() ?? '' } : {}),
    handle: 'none',
    softClose: false,
    pieces,
  };
}

async function standardOrder(standard: StandardCabinets, spec: CabinetOrderSpec) {
  const units = await standard.match(spec.cabinets);
  const needsFronts = spec.cabinets.some((c) => c.fronts);
  const painted = spec.colourId !== 'ral' || (spec.paintColour?.trim().length ?? 0) >= 2;
  const frontsPriced = !needsFronts || (Boolean(spec.rangeId) && Boolean(spec.colourId) && painted);
  if (units.some((u) => !u) || !frontsPriced) throw new NeedsPriceRequest(units.map((u) => (u ? name(u) : undefined)));
  return units as CabinetProduct[];
}

const pence = (n: number) => Math.round(n * 100) / 100;

export function liveCabinetPricing(standard: StandardCabinets, doorPricing: DoorPricing): CabinetPricing {
  return {
    async quote(spec): Promise<CabinetQuote> {
      const units = await standardOrder(standard, spec);
      // A cabinet whose fronts the shop refuses (a size its range does not make) is priced on
      // request with the shop's reason; the rest of the order is not lost to it.
      const reasons: Array<string | undefined> = spec.cabinets.map(() => undefined);
      const lines = await Promise.all(
        spec.cabinets.map(async (c, i) => {
          const unit = units[i]!;
          const order = frontsOrder(spec, c, unit);
          let doors = null;
          if (order) {
            try {
              doors = await doorPricing.quote(order);
            } catch (error) {
              if (!(error instanceof ApiError) || error.status >= 500) throw error;
              reasons[i] = error.message;
              return null;
            }
          }
          // Per cabinet: the door quote covers all `qty` cabinets' fronts.
          const fronts = doors ? doors.piecesTotal / c.qty : 0;
          const hardware = doors ? doors.extrasTotal / c.qty : 0;
          const carcass = unit.price!;
          const total = pence(carcass * c.qty + (doors?.total ?? 0));
          return { standard: name(unit), carcass, fronts: pence(fronts), hardware: pence(hardware), unit: pence(total / c.qty), total };
        }),
      );
      if (reasons.some(Boolean)) {
        throw new NeedsPriceRequest(units.map((u, i) => (reasons[i] ? undefined : name(u))), reasons);
      }
      const priced = lines as NonNullable<(typeof lines)[number]>[];
      return {
        currency: 'GBP',
        lines: priced,
        cabinets: spec.cabinets.reduce((s, c) => s + c.qty, 0),
        total: pence(priced.reduce((s, l) => s + l.total, 0)),
      };
    },
  };
}

export interface CabinetBasketAdapter {
  add(spec: CabinetOrderSpec): Promise<void>;
}

/**
 * Adds each standard cabinet's carcass product, then its fronts as a door
 * order, to the shopper's basket. Refuses a modified cabinet before anything
 * is added, so a mixed order never lands half in the basket.
 */
export function liveCabinetBasket(
  standard: StandardCabinets,
  doorBasket: { add(spec: DoorOrderSpec): Promise<void> },
  fetchImpl?: Fetch,
  tokens: CartTokenStore = browserCartTokens,
): CabinetBasketAdapter {
  const api = http(fetchImpl);
  return {
    async add(spec) {
      const units = await standardOrder(standard, spec);
      for (const [i, c] of spec.cabinets.entries()) {
        const unit = units[i]!;
        await withBasket(api, tokens, (token) =>
          api.send<{ token?: string }>('POST', `${API_ROOT}/cart/${encodeURIComponent(token)}/items`, {
            productId: unit.productId,
            quantity: String(c.qty),
          }),
        );
        const order = frontsOrder(spec, c, unit);
        if (order) await doorBasket.add(order);
      }
    },
  };
}
